<template>
  <div class="pedidos-page">
    <modal_pdf_pedido ref="modal_pdf_pedido"/>

    <nav class="page-breadcrumb">
      <router-link :to="{ name: 'dashboard-ptc' }" class="breadcrumb-link">
        <feather-icon icon="HomeIcon" size="14" />
        <span>Operações</span>
      </router-link>
      <feather-icon icon="ChevronRightIcon" size="14" class="breadcrumb-separator" />
      <span class="breadcrumb-current">Pedidos de Vendas</span>
    </nav>

    <KpiCards
      v-show="activeTab === 0"
      :status-summary="kpiStatusSummary"
      :selected-status="statusFilter"
      @filter="handleKpiFilter"
    />

    <TopActionBar
      :current-view="currentView"
      :active-filters-count="activeFiltersCount"
      :active-tab="activeTab"
      :total-pedidos="totalPedidos"
      :total-rascunhos="totalRascunhos"
      :is-loading="isLoading"
      :page-size="getMeta.pageSize || 20"
      @toggle-filters="showFiltersDrawer = true"
      @change-view="changeView"
      @change-tab="changeTab"
      @change-page-size="handlePageSizeChange"
      @new-order="$router.push({ name: 'pedidos-vendas-create' })"
    />

    <FiltersDrawer
      :is-open="showFiltersDrawer"
      :filters="filtersData"
      @close="showFiltersDrawer = false"
      @apply="applyFilters"
    />

    <div class="content-container">
      <div v-show="activeTab === 0">
        <template v-if="currentView === 'list'">
          <pedidos
            :hide-header-controls="true"
            @paginateFilters="paginateFiltersPedidos"
            @view-details="viewOrderDetails"
            @generate-pdf="generatePdf"
          />
        </template>
        <template v-else-if="currentView === 'kanban'">
          <KanbanView
            :orders="getPedidoVendas"
            @view-details="viewOrderDetails"
          />
        </template>
        <template v-else-if="currentView === 'cards'">
          <CardsView
            :orders="getPedidoVendas"
            @view-details="viewOrderDetails"
            @generate-pdf="generatePdf"
          />
        </template>
      </div>
      <div v-show="activeTab === 1">
        <rascunhos
          :hide-header-controls="true"
          @paginateFilters="paginateFiltersRascunhos"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Modal_pdf_pedido from '@/views/app/operacoes/pedidosVendas/modal/modal_pdf_pedido.vue';
import Pedidos from '@/views/app/operacoes/pedidosVendas/pedidos.vue';
import Rascunhos from '@/views/app/operacoes/pedidosVendas/rascunhos.vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import KpiCards from './components/KpiCards.vue';
import TopActionBar from './components/TopActionBar.vue';
import FiltersDrawer from './components/FiltersDrawer.vue';
import KanbanView from './components/KanbanView.vue';
import CardsView from './components/CardsView.vue';
import StatusPedidoService from '@/router/status-pedidos/status-pedidos.service';

const VIEW_STORAGE_KEY = 'pedidosVendas_viewPreference';
const statusPedidoService = new StatusPedidoService();

export default {
  components: {
    KpiCards,
    TopActionBar,
    FiltersDrawer,
    KanbanView,
    CardsView,
    Modal_pdf_pedido,
    Pedidos,
    Rascunhos,
  },
  data() {
    return {
      activeTab: 0,
      currentView: 'list',
      showFiltersDrawer: false,
      statusFilter: null,
      rascunhosTotal: 0,
      filtersData: {
        dataInicial: '',
        dataFinal: '',
        prePedido: null,
        cliente: null,
        razaoSocial: '',
        pedidoErp: null,
        status: null,
      },
      kpiStatusSummary: [],
    };
  },
  computed: {
    ...mapGetters('SLS_PedidosVendas', ['getFilters', 'getPedidoVendas', 'getMeta']),
    isLoading() {
      return this.$store.getters['SLS_PedidosVendas/getIsLoading'];
    },
    totalPedidos() {
      return this.activeTab === 0 ? (this.getMeta?.total || 0) : this._cachedPedidosTotal || 0;
    },
    totalRascunhos() {
      return this.activeTab === 1 ? (this.getMeta?.total || 0) : this.rascunhosTotal;
    },
    activeFiltersCount() {
      let count = 0;
      if (this.filtersData.prePedido) count++;
      if (this.filtersData.pedidoErp) count++;
      if (this.filtersData.cliente) count++;
      if (this.filtersData.razaoSocial) count++;
      if (this.filtersData.status) count++;
      if (this.filtersData.dataInicial) count++;
      if (this.filtersData.dataFinal) count++;
      if (this.statusFilter) count++;
      return count;
    },
  },
  methods: {
    ...mapActions('SLS_PedidosVendas', ['setFilters', 'retornaPedidoVendas', 'retornaRascunhosPedidoVendas', 'clearFilters']),

    loadViewPreference() {
      try {
        const saved = localStorage.getItem(VIEW_STORAGE_KEY);
        if (saved && ['list', 'kanban', 'cards'].includes(saved)) {
          this.currentView = saved;
        }
      } catch (e) {
        console.warn('Could not load view preference');
      }
    },

    saveViewPreference() {
      try {
        localStorage.setItem(VIEW_STORAGE_KEY, this.currentView);
      } catch (e) {
        console.warn('Could not save view preference');
      }
    },

    changeView(view) {
      this.currentView = view;
      this.saveViewPreference();
    },

    async handlePageSizeChange(newSize) {
      const currentFilters = this.getFilters || {};
      const mergedFilters = { ...currentFilters, pageSize: newSize, page: 1 };
      await this.setFilters(mergedFilters);

      if (this.activeTab === 0) {
        await this.retornaPedidoVendas();
      } else {
        await this.retornaRascunhosPedidoVendas();
      }
    },

    verificarPerfilUsuario() {
      try {
        const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado'));

        const filtros = {};

        if (perfilLogado.perfil_acesso_nome === 'Representante') {
          const representanteData = JSON.parse(localStorage.getItem('representanteData'));
          if (representanteData && representanteData.representanteid) {
            filtros.perfilTipo = 'Representante';
            filtros.perfilId = representanteData.representanteid;
          }
        }

        if (perfilLogado.perfil_acesso_nome === 'ClienteFinal') {
          const clienteData = JSON.parse(localStorage.getItem('clienteData'));
          if (clienteData && clienteData.cliente.clienteid) {
            filtros.perfilTipo = 'ClienteFinal';
            filtros.perfilId = clienteData.cliente.clienteid;
          }
        }

        return filtros;
      } catch (error) {
        console.error('Erro ao verificar perfil do usuário:', error);
        return {};
      }
    },

    async fetchStatusSummaryForKpi() {
      try {
        const filtrosPerfil = this.verificarPerfilUsuario();
        const filters = { ...this.filtersData };

        Object.keys(filters).forEach((key) => {
          if (filters[key] === null || filters[key] === '') {
            delete filters[key];
          }
        });

        // Removendo filtros que não devem afetar a totalização dos cards
        delete filters.status;
        delete filters.page;
        delete filters.pageSize;

        const combinedFilters = { ...filtrosPerfil, ...filters };

        const response = await statusPedidoService.findAll(combinedFilters);
        if (Array.isArray(response)) {
          this.kpiStatusSummary = response;
        } else {
          this.kpiStatusSummary = [];
        }
      } catch (e) {
        console.warn('Could not fetch status summary for KPI:', e);
        this.kpiStatusSummary = [];
      }
    },

    async handleKpiFilter(status) {
      if (!status) {
        this.statusFilter = null;
        this.filtersData.status = null;
        this.filtersData.page = 1;
        await this.setFilters({ status: null });
        await this.search();
        return;
      }
      this.statusFilter = status;
      this.filtersData.status = status;
      this.filtersData.page = 1;
      await this.search();
    },

    async search() {
      const filters = { ...this.filtersData };

      Object.keys(filters).forEach((key) => {
        if (filters[key] === null || filters[key] === '') {
          delete filters[key];
        }
      });

      await this.setFilters(filters);

      if (this.activeTab === 0) {
        await this.retornaPedidoVendas();
        this._cachedPedidosTotal = this.getMeta?.total || 0;
        await this.fetchStatusSummaryForKpi();
      } else {
        await this.retornaRascunhosPedidoVendas();
      }
    },

    async applyFilters(filters) {
      this.filtersData = { ...filters, page: 1 };
      if (filters.status) {
        this.statusFilter = filters.status;
      } else {
        this.statusFilter = null;
      }
      await this.setFilters({ ...filters, status: filters.status ?? null, page: 1 });
      await this.search();
    },

    async handleClearFilters() {
      this.filtersData = {
        dataInicial: '',
        dataFinal: '',
        prePedido: null,
        cliente: null,
        razaoSocial: '',
        pedidoErp: null,
        status: null,
      };
      this.statusFilter = null;

      await this.setFilters({});

      if (this.activeTab === 0) {
        await this.retornaPedidoVendas();
        await this.fetchStatusSummaryForKpi();
      } else {
        await this.retornaRascunhosPedidoVendas();
      }
    },

    async paginateFiltersPedidos(newFilters) {
      const currentFilters = this.getFilters || {};
      const mergedFilters = { ...currentFilters, ...newFilters };
      await this.setFilters(mergedFilters);
      await this.retornaPedidoVendas();
    },

    async paginateFiltersRascunhos(newFilters) {
      const currentFilters = this.getFilters || {};
      const mergedFilters = { ...currentFilters, ...newFilters };
      await this.setFilters(mergedFilters);
      await this.retornaRascunhosPedidoVendas();
    },

    async changeTab(tabIndex) {
      if (this.isLoading) {
        return;
      }

      if (this.activeTab === 0) {
        this._cachedPedidosTotal = this.getMeta?.total || 0;
      }

      this.activeTab = tabIndex;

      if (tabIndex === 0) {
        await this.retornaPedidoVendas();
      } else if (tabIndex === 1) {
        await this.retornaRascunhosPedidoVendas();
        this.rascunhosTotal = this.getMeta?.total || 0;
      }
    },

    async viewOrderDetails(item) {
      await this.$router.push({
        name: 'pedidos-vendas-detalhes',
        params: { pedidoid: item.pedidoId, clienteid: item.clienteId },
      });
    },

    generatePdf(item) {
      if (this.$refs.modal_pdf_pedido) {
        this.$refs.modal_pdf_pedido.showModalPdf(item);
      }
    },
  },
  async mounted() {
    this.loadViewPreference();
    const filtrosPerfil = this.verificarPerfilUsuario();
    await this.setFilters(filtrosPerfil);
    await this.search();
    this._cachedPedidosTotal = this.getMeta?.total || 0;
    await this.fetchStatusSummaryForKpi();
  },
};
</script>

<style scoped>
.pedidos-page {
  padding: 0.5rem 0.75rem;
  min-height: 100vh;
}

.page-breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
}

.breadcrumb-link {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--sf-text-muted);
  text-decoration: none;
  transition: color 0.15s ease;
}

.breadcrumb-link:hover {
  color: var(--sf-accent);
}

.breadcrumb-separator {
  color: var(--sf-text-muted);
}

.breadcrumb-current {
  color: var(--sf-text-secondary);
  font-weight: 500;
}

.content-container {
  background: var(--sf-card-bg);
  border-radius: 8px;
  border: 1px solid var(--sf-card-border);
  overflow: hidden;
  box-shadow: var(--sf-card-shadow);
}

@media (max-width: 768px) {
  .pedidos-page {
    padding: 0.5rem;
  }
}

/* Theme-aware via CSS variables (no extra dark override needed) */
</style>
